/**
 * Currency detection and formatting utilities
 *
 * Primary:  IP geolocation via Cloud Function (x-appengine-country header)
 * Fallback: Browser timezone (if API call fails or hasn't completed yet)
 *
 * Flow:
 * 1. detectCurrency() is synchronous — reads from localStorage, falls back to timezone
 * 2. fetchAndCacheCurrency() is async — calls /api/detect-country, updates localStorage
 * 3. App.tsx calls fetchAndCacheCurrency() on mount; subsequent page loads use the cached value
 */

export type SupportedCurrency = 'GBP' | 'EUR' | 'USD';

export interface CurrencyConfig {
  code: SupportedCurrency;
  symbol: string;
  locale: string;
}

const CURRENCY_CONFIGS: Record<SupportedCurrency, CurrencyConfig> = {
  GBP: { code: 'GBP', symbol: '£', locale: 'en-GB' },
  EUR: { code: 'EUR', symbol: '€', locale: 'en-IE' },
  USD: { code: 'USD', symbol: '$', locale: 'en-US' },
};

const STORAGE_KEY = 'trainedup_currency';

// EU member states + EEA + Eurozone micro-states
const EU_COUNTRY_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA / European non-EU
  'NO', 'IS', 'LI', 'CH', 'AD', 'MC', 'SM', 'VA', 'ME', 'AL',
  'BA', 'MK', 'RS', 'XK', 'MD', 'UA',
]);

const UK_COUNTRY_CODES = new Set(['GB', 'IM', 'JE', 'GG']);

/** Map a 2-letter country code to a currency */
function countryToCurrency(country: string): SupportedCurrency {
  const code = country.toUpperCase();
  if (UK_COUNTRY_CODES.has(code)) return 'GBP';
  if (EU_COUNTRY_CODES.has(code)) return 'EUR';
  return 'USD';
}

/** Timezone fallback: UK timezones → GBP, Europe/* → EUR, else USD */
function detectFromTimezone(): SupportedCurrency {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (['Europe/London', 'Europe/Belfast', 'Europe/Guernsey', 'Europe/Isle_of_Man', 'Europe/Jersey'].includes(tz)) {
      return 'GBP';
    }
    if (tz.startsWith('Europe/')) {
      return 'EUR';
    }
  } catch {
    // ignore
  }
  return 'USD';
}

/**
 * Synchronous currency detection.
 * Reads from localStorage cache; falls back to timezone if no cache exists.
 * Call fetchAndCacheCurrency() on app init to populate the cache from IP geolocation.
 */
export function detectCurrency(): SupportedCurrency {
  try {
    const cached = localStorage.getItem(STORAGE_KEY) as SupportedCurrency | null;
    if (cached && CURRENCY_CONFIGS[cached]) return cached;
  } catch {
    // localStorage unavailable
  }

  // No cache yet — use timezone as initial guess
  const fallback = detectFromTimezone();

  try {
    localStorage.setItem(STORAGE_KEY, fallback);
  } catch {
    // localStorage unavailable
  }

  return fallback;
}

/**
 * Async: call Cloud Function to get country from IP, then cache the currency.
 * Should be called once on app startup (e.g., in App.tsx useEffect).
 * Returns true if the currency changed from the previous cached value.
 */
export async function fetchAndCacheCurrency(): Promise<boolean> {
  try {
    const res = await fetch('/api/detect-country', { method: 'GET' });
    if (!res.ok) return false;

    const data = await res.json();
    const country = (data.country || '') as string;
    if (!country) return false;

    const newCurrency = countryToCurrency(country);
    const oldCurrency = detectCurrency();

    if (newCurrency !== oldCurrency) {
      try { localStorage.setItem(STORAGE_KEY, newCurrency); } catch { /* ignore */ }
      return true; // currency changed
    }

    return false;
  } catch {
    // API unavailable (e.g., localhost, offline) — timezone fallback already cached
    return false;
  }
}

export function getCurrencyConfig(currency?: SupportedCurrency): CurrencyConfig {
  return CURRENCY_CONFIGS[currency || detectCurrency()];
}

/**
 * Format a price amount (in minor units) for display.
 * e.g. formatPrice(1299, 'GBP') => '£12.99'
 */
export function formatPrice(amountMinor: number, currency?: SupportedCurrency): string {
  const config = getCurrencyConfig(currency);
  return `${config.symbol}${(amountMinor / 100).toFixed(2)}`;
}

/**
 * Allow user to explicitly override detected currency.
 */
export function setCurrency(currency: SupportedCurrency): void {
  try {
    localStorage.setItem(STORAGE_KEY, currency);
  } catch {
    // localStorage unavailable
  }
}
